/* -------------------------------------------------------------------------
 * /platforms: the region pills, and the Details dialog behind every locked
 * fee and every Details while signed out. The server draws the rows, the
 * pills and the dialog's frame; this keeps them in step with ?region= and
 * fills the dialog from #pf-data, which carries no fee. Signed in, Open is a
 * plain link to the platform's site.
 * ---------------------------------------------------------------------- */
import type { Region } from '../data';
import {
  closesIn,
  dialogBody,
  dialogEmpty,
  dialogHead,
  dialogTitle,
  inRegion,
  parseRegion,
  type DetailsTarget,
  type RegionFilter,
} from '../platforms';
import { $, $$, attr, text } from './dom';
import { track } from './events';
import { reducedMotion } from './motion';
import { addOnboardingRegion } from './onboarding';

export interface PlatformsDeps {
  now: () => number;
}

const DEFAULT_DEPS: PlatformsDeps = { now: () => Date.now() };

function readTargets(): Map<string, DetailsTarget> {
  try {
    const raw = document.getElementById('pf-data')?.textContent ?? '[]';
    const list = JSON.parse(raw) as DetailsTarget[];
    return new Map(list.map((t) => [t.id, t]));
  } catch {
    return new Map();
  }
}

export function platformsPage(
  root: HTMLElement,
  deps: PlatformsDeps = DEFAULT_DEPS,
): void {
  const signedIn = root.hasAttribute('data-signed-in');
  const targets = readTargets();
  const rows = $$(root, '[data-pf-row]');

  /* ---- Regions --------------------------------------------------------- */
  const pills = $$<HTMLButtonElement>(root, '[data-pf-regions] [data-region]');
  const regionInUrl = () =>
    parseRegion(new URL(window.location.href).searchParams.get('region'));
  const applyRegion = (region: RegionFilter) => {
    for (const pill of pills) {
      attr(pill, 'aria-pressed', String(pill.dataset.region === region));
    }
    for (const row of rows) {
      const own = (row.dataset.region || null) as Region | null;
      row.hidden = !inRegion(own, region);
    }
  };
  for (const pill of pills) {
    pill.addEventListener('click', (e) => {
      // The pills submit ?region= without the script; with it, no reload.
      e.preventDefault();
      const region = parseRegion(pill.dataset.region);
      applyRegion(region);
      if (region === regionInUrl()) return;
      const url = new URL(window.location.href);
      if (region === 'All') url.searchParams.delete('region');
      else url.searchParams.set('region', region);
      history.pushState(null, '', url);
      track('platforms_region_filtered', { region });
    });
  }
  // Back and Forward walk the filters the reader chose.
  window.addEventListener('popstate', () => applyRegion(regionInUrl()));

  /* ---- The buttons ----------------------------------------------------- */
  const dlg = signedIn ? null : detailsDialog(root, deps);
  const openFor = (id: string, from: HTMLElement) => {
    const target = targets.get(id);
    if (target && dlg) dlg.open(target, from);
  };
  for (const row of rows) {
    const id = row.dataset.id ?? '';
    $(row, '[data-pf-unlock]')?.addEventListener('click', (e) => {
      track('platforms_fee_unlock_clicked', { platform: id });
      openFor(id, e.currentTarget as HTMLElement);
    });
    $(row, '[data-pf-details]')?.addEventListener('click', (e) => {
      track('platforms_details_clicked', { platform: id });
      openFor(id, e.currentTarget as HTMLElement);
    });
    $(row, '[data-pf-open]')?.addEventListener('click', () => {
      track('platforms_open_clicked', { platform: id });
    });
  }
  $(root, '[data-pf-signup]')?.addEventListener('submit', () => {
    // The form goes on to the dashboard's sign-up card itself.
    track('platforms_signup_submitted', { source: 'hero' });
  });
  $(root, '[data-pf-closing]')?.addEventListener('click', () => {
    track('platforms_signup_submitted', { source: 'closing' });
  });

  track('platforms_viewed', { region: regionInUrl(), signed_in: signedIn });
}

/**
 * The Details dialog: modal, labelled by its title, focus held inside it and
 * starting on Create free account, Escape or the scrim to close, the page
 * under it still, and focus back on what opened it.
 */
function detailsDialog(root: HTMLElement, deps: PlatformsDeps) {
  const scrim = $(root, '[data-pf-scrim]');
  const box = $(root, '[data-pf-dialog]');
  const list = $(root, '[data-dlg-list]');
  const empty = $(root, '[data-dlg-empty]');
  const signup = $<HTMLAnchorElement>(root, '[data-dlg-signup]');
  if (!scrim || !box || !list || !empty || !signup) return null;

  let current: DetailsTarget | null = null;
  let opener: HTMLElement | null = null;
  let timer = 0;

  const paintTimes = () => {
    for (const el of $$(list, '[data-closes]')) {
      const c = closesIn(Number(el.dataset.closes), deps.now());
      text(el, c.text);
      attr(el, 'aria-label', c.label);
      el.className = `num pf-dlg-cd is-${c.urgency ?? 'none'}`;
    }
  };

  const focusables = () =>
    $$<HTMLElement>(box, 'a[href], button:not([disabled])');

  const close = () => {
    if (!current) return;
    current = null;
    scrim.hidden = true;
    document.documentElement.classList.remove('pf-locked');
    window.clearInterval(timer);
    opener?.focus();
    opener = null;
  };

  const open = (target: DetailsTarget, from: HTMLElement) => {
    current = target;
    opener = from;
    text($(box, '[data-dlg-mono]'), target.mono);
    text($(box, '[data-dlg-title]'), dialogTitle(target.name));
    text($(box, '[data-dlg-body]'), dialogBody(target.name));
    text($(box, '[data-dlg-head]'), dialogHead(target.name, target.liveCount));
    list.replaceChildren(
      ...target.live.map((raise) => {
        const item = document.createElement('li');
        const name = document.createElement('span');
        name.className = 'pf-dlg-raise';
        name.textContent = raise.name;
        name.title = raise.name;
        const when = document.createElement('span');
        when.setAttribute('role', 'timer');
        when.setAttribute('aria-live', 'off');
        if (raise.closesAt !== null)
          when.dataset.closes = String(raise.closesAt);
        const c = closesIn(raise.closesAt, deps.now());
        when.className = `num pf-dlg-cd is-${c.urgency ?? 'none'}`;
        when.textContent = c.text;
        when.setAttribute('aria-label', c.label);
        item.append(name, when);
        return item;
      }),
    );
    empty.hidden = target.live.length > 0;
    text(empty, target.live.length ? '' : dialogEmpty(target.planned));

    scrim.hidden = false;
    document.documentElement.classList.add('pf-locked');
    window.clearInterval(timer);
    timer = window.setInterval(paintTimes, reducedMotion() ? 60_000 : 1000);
    signup.focus();
  };

  scrim.addEventListener('click', (e) => {
    if (e.target === scrim) close();
  });
  $(box, '[data-dlg-close]')?.addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (!current) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = focusables();
    const first = items[0];
    const last = items[items.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || !box.contains(at))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (at === last || !box.contains(at))) {
      e.preventDefault();
      first.focus();
    }
  });
  // Focus that lands outside (a click on the scrim's edge) comes back in.
  document.addEventListener('focusin', (e) => {
    if (current && !box.contains(e.target as Node)) signup.focus();
  });
  signup.addEventListener('click', () => {
    track('platforms_signup_submitted', { source: 'dialog' });
    if (current?.region) addOnboardingRegion(current.region);
  });

  return { open, close };
}
